import React, { createContext, useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { lightTheme, darkTheme } from '../themes';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  toggleTheme: () => void;
  mode: ThemeMode;
}

export const ThemeContext = createContext<ThemeContextType>({
  toggleTheme: () => { },
  mode: 'dark',
});

export const useThemeManager = () => useContext(ThemeContext);

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try {
      const storedMode = window.localStorage.getItem('themeMode') as ThemeMode | null;
      return storedMode || 'dark'; // Default to dark mode
    } catch (error) {
      return 'dark';
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('themeMode', mode);
    } catch (error) {
      console.error('Failed to save theme mode to localStorage:', error);
    }
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  }, []);

  const theme = useMemo(() => (mode === 'light' ? lightTheme : darkTheme), [mode]);

  const contextValue = useMemo(() => ({
    toggleTheme,
    mode,
  }), [toggleTheme, mode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};